import {computed, inject, Injectable, signal, Signal} from '@angular/core';
import {SearchService} from '../data/search.service';
import {Facets, FacetKey, FacetNode} from '../domain/search.model';
import {toSignal} from '@angular/core/rxjs-interop';
import {map, scan, Subject, switchMap} from 'rxjs';
import {Scheme} from '../../scheme/domain/scheme.model';

interface SearchTrigger {
  cursor: string | null;
  filters: Facets | null;
  query: string | null;
  reset: boolean;
}

interface SearchState {
  schemes: Scheme[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
  facets: Facets;
}

const createEmptyFacets = (): Facets => ({
  [FacetKey.PRODUCTS]: [],
  [FacetKey.PRODUCTS_CASCADE]: [],
  [FacetKey.CONTROL_PLATFORMS]: [],
});

const initialState: SearchState = {
  schemes: [],
  total: 0,
  hasMore: false,
  nextCursor: null,
  facets: createEmptyFacets(),
};

@Injectable({ providedIn: 'root' })
export class SearchFacade {
  private readonly _searchService = inject(SearchService);

  private readonly _country = 'DE';
  private readonly _language = 'de';

  private readonly _activeFilters = signal<Facets>(createEmptyFacets());
  private _currentQuery: string | null = null;

  private readonly _trigger$ = new Subject<SearchTrigger>();

  private readonly _state = toSignal(
    this._trigger$.pipe(
      switchMap(trigger =>
        this._searchService.search({
          country: this._country,
          language: this._language,
          filters: trigger.filters,
          query: trigger.query,
          cursor: trigger.cursor,
        }).pipe(
          map(result => ({ result, reset: trigger.reset }))
        )
      ),
      scan((acc: SearchState, { result, reset }) => ({
        schemes: reset ? result.items : [...acc.schemes, ...result.items],
        total: reset ? result.total : acc.total,
        hasMore: result.page.hasMore,
        nextCursor: result.page.nextCursor,
        // Cursor responses don't return facets or totals – keep the previous ones
        facets: result.facets ?? acc.facets,
      }), initialState)
    ),
    { initialValue: initialState }
  );

  public readonly schemes: Signal<Scheme[]> = computed(() => this._state().schemes);
  public readonly totalSchemesCount: Signal<number> = computed(() => this._state().total);
  public readonly facetNodes: Signal<Facets> = computed(() => this._state().facets);
  public readonly activeFilters: Signal<Facets> = computed(() => this._activeFilters());
  public readonly hasActiveFilters: Signal<boolean> = computed(() =>
    Object.values(this._activeFilters()).some(nodes => nodes.length > 0)
  );

  constructor() {
    this._trigger$.next({ cursor: null, filters: null, query: null, reset: true });
  }

  public search(key: FacetKey, facets: FacetNode[]): void {
    const nextFilters = {
      ...this._activeFilters(),
      [key]: facets,
    };
    this._activeFilters.set(nextFilters);
    this._trigger$.next({ cursor: null, filters: nextFilters, query: this._currentQuery, reset: true });
  }

  public searchByQuery(query: string | null): void {
    this._currentQuery = query;
    this._trigger$.next({ cursor: null, filters: this._activeFilters(), query: this._currentQuery, reset: true });
  }

  public resetFilters(): void {
    this._activeFilters.set(createEmptyFacets());
    this._trigger$.next({ cursor: null, filters: null, query: this._currentQuery, reset: true });
  }

  public searchNext(): void {
    const { hasMore, nextCursor } = this._state();
    if (!hasMore || !nextCursor) return;
    this._trigger$.next({ cursor: nextCursor, filters: null, query: null, reset: false });
  }
}
